/**
 * **Modifier folding** — the passive kind of the three-kind taxonomy. A `modifier` effect is a
 * *continuous condition over computation*: it **emits no steps**, its contribution folds into
 * the stat/roll math at resolve time (the modified accuracy shows up baked into the `to_hit`
 * step). This is the overlay-data home statuses live in: an Impaired *modifier* contributes −1
 * to the carrier's attacks; a Lock On *modifier* contributes +1 to attackers vs the carrier and
 * consumes on hit; the advisory gates (Stunned/Slowed/…) contribute only an `advisory` message
 * (no math). The resolver reads these off the overlay ONLY — there is no parallel status table.
 *
 * `condition`-gating is evaluated against a {@link PredicateContext}: a modifier with an unmet
 * condition contributes nothing. A `gm_ruling` with no recorded answer halts an interactive
 * resolution to ask the GM, and reads `false` elsewhere.
 */

import { resourceCapRawKey, type AttackVerb, type EncounterState, type DamageType } from "@lancer-vtt/domain";
import type { ModifierEffect, ImmunityScope, ModifierContribution, SaveCheckRoll } from "./atoms";
import { evaluatePredicate, type AttackEvent, type CoverDegree, type PredicateContext } from "./predicate";
import type { ChoiceSource } from "./choices";
import { evaluateMatch } from "./family";
import { affordable } from "../events/gameEvents";

/**
 * The accumulated contribution of a set of `modifier` effects to attack resolution — the shape
 * the resolver nets into the to-hit math + warnings. Every field is a sum over the modifiers
 * that applied.
 */
export type ModifierContributionSum = {
  /** Net accuracy/difficulty the carrier grants itself when attacking (Impaired = −1). */
  attackerAccuracy: number;
  /** Net accuracy/difficulty an attacker gets vs the carrier (Lock On = +1). */
  targetAccuracy: number;
  /**
   * The per-source breakdown behind {@link attackerAccuracy}/{@link targetAccuracy} — see
   * `ToHitStep.accuracyContributions`'s doc for the full rationale. One entry per modifier
   * whose `attackerAccuracy`/`targetAccuracy` facet actually folded — pushed in the
   * SAME branch, guarded by the SAME truthy check, as the aggregate `+=` just below
   * it, so a gated modifier that `continue`d past its `match`/`condition` above
   * never reaches either. Empty when nothing folded (the common case).
   */
  accuracyContributions: { name: string; carrier: string; side: "attacker" | "target"; value: number }[];
  /** Status names to consume on hit (Lock On) — the carrier-side modifier's one emitted step. */
  consumeOnHit: string[];
  /**
   * `modifier_consume_resource_on_apply` — every capped modifier that ACTUALLY folded this
   * resolution (its gate held, so its contribution counted toward `attackerAccuracy`/etc.). The
   * carrier + raw `key` the caller turns into a `resource_delta` +1 step (`resolve/attack.ts`, spliced
   * onto `Resolved`'s own `resourceCapConsumeSteps` at the ROOM layer — never folded into
   * `resolve()`'s own `command.steps`, mirroring `ordinalIncrement`). Empty when no modifier
   * this fold touched carries the facet (the overwhelming common case today).
   */
  consumedResourceCaps: { carrier: string; key: string }[];
  /** True iff a target-side modifier makes an attack ignore the carrier's Armor (Shredded). */
  ignoreTargetArmor: boolean;
  /** True iff a target-side modifier doubles physical damage against the carrier (Exposed). */
  doubleDamage: boolean;
  /**
   * Net accuracy/difficulty the carrier gets on its **next save or skill check** (Bolster
   * = +2). A carrier-self contribution — accumulated side-agnostically (a save has no
   * attacker/target side), folded into the save's accuracy dice by `saves.ts`.
   */
  saveCheckBonus: number;
  /**
   * The per-source breakdown behind {@link saveCheckBonus} — the save/check analogue of
   * {@link accuracyContributions}, and the same
   * single-guard discipline: one entry per modifier whose `saveCheckBonus` facet actually
   * folded, pushed in the SAME branch, guarded by the SAME truthy check, as the aggregate
   * `+=` beside it. That co-location is the structural guarantee that the total and its
   * named parts cannot diverge — a modifier that `continue`d past its `match`/`condition`
   * above, or whose save facet `scopeSaveModifiers` already stripped, reaches neither.
   *
   * No `side` field, unlike `accuracyContributions`: the save/check dimension is
   * carrier-self and accumulates side-agnostically (see {@link saveCheckBonus}), so the
   * entries carry `SaveStep`'s own `SaveContribution` shape. Empty when nothing folded
   * (the common case) — the emitters omit the step field entirely rather than record `[]`.
   */
  saveCheckContributions: { name: string; carrier: string; value: number }[];
  /** Status names the carrier consumes when it makes a save/skill check (Bolster) — the save/check analogue of `consumeOnHit`. */
  consumeOnSaveCheck: string[];
  /**
   * Advisory warnings raised when the carrier is the **attacker** (Stunned/Slowed/… — surfaced,
   * never blocking). Only collected on the `attacker` side.
   */
  advisories: string[];
  /**
   * Deferred spatial riders noted on the carrier's modifiers (informational — Prone's adjacency
   * rider).
   */
  spatialRidersDeferred: string[];
  /**
   * Immunity scopes the carrier holds, accumulated across every
   * modifier that carries the facet. A **carrier-self** contribution — like
   * {@link saveCheckBonus}, collected side-agnostically (immunity has no attacker/target
   * side; it's a property of whoever carries Shut Down/Biological/a grant). Read by
   * `immuneToTechAction` (`seed.ts`), which folds the `tech`-scoped entries into a
   * per-action predicate; the `damage`/`status` scopes are read by that same file's
   * `immuneToDamage`/`statusBlockedByImmunity`.
   */
  immunity: ImmunityScope[];
  /**
   * Resistance grants the carrier holds, accumulated across every modifier that carries the
   * facet — each entry either `"all"` or a specific {@link DamageType}. A **carrier-self**
   * contribution — like {@link immunity}, collected side-agnostically (Resistance has no
   * attacker/target side; it's a property of whoever carries the grant). Read by
   * `foldedResistance` (`seed.ts`), unified with Brace's built-in `resistant` flag at the
   * mitigate read-site.
   */
  resistance: ("all" | DamageType)[];
  /**
   * Stunned's Evasion cap — the MOST restrictive
   * (lowest) cap across every target-side modifier that carries the facet. `undefined`
   * when no active modifier caps Evasion (the common case) — distinct from a cap of `0`,
   * so the resolver's `min(evasion, cap)` fold only engages when this is set.
   */
  evasionCap?: number;
  /**
   * Stunned's auto-fail HULL/AGI checks and saves — a
   * **carrier-self** trait, accumulated side-agnostically like {@link saveCheckBonus}/
   * {@link immunity}/{@link resistance}: every named stat across every modifier the
   * carrier holds. Empty when nothing auto-fails (the common case).
   */
  autoFailChecks: string[];
  /** `armorPiercing` (Walking Armory's SABOT) — an ATTACKER-side modifier makes the attack AP. */
  armorPiercing: boolean;
  /** `bonusDamageType` (Walking Armory's HELLFIRE) — the type an ATTACKER-side modifier gives the attack's bonus damage. */
  bonusDamageType: DamageType | undefined;
  /**
   * `override_attack_damage_type` — an ATTACKER-side modifier
   * overriding the resolution's damage type outright (t_walking_armory:1's ammo rounds).
   * `undefined` when no active modifier overrides it (the common case — the frozen weapon
   * field/variable-choose prompt drives `dtype` as before). The LAST folded override wins on
   * a same-fold collision (an unlikely double-grant; no shipped content exercises it).
   */
  overrideDamageType?: DamageType;
  /** `ignoreCover` (Vanguard 2): `true` iff any qualifying attacker-side modifier makes the attack ignore cover. */
  ignoreCover: boolean;
  /**
   * `substituteStructureDamage` (Sniper's Mark) — `true` iff any qualifying TARGET-side modifier
   * this fold applied replaces the hit's whole damage application with 1 structure damage.
   * Accumulated as a boolean OR: two sources cannot stack past "1 structure point instead of the
   * damage," which is the whole of RAW's sentence. See
   * {@link import("./atoms").ModifierContribution.substituteStructureDamage} for the read site and
   * everything the substitution skips.
   */
  substituteStructureDamage: boolean;
  /**
   * `cannotAttack` (the rig's Unarmed) — the entity id of the first modifier this fold applied
   * that says the carrier cannot make attacks, or `undefined`. Carrier-self and side-agnostic,
   * like `immunity`; the id is what the refusal names. See
   * {@link import("./atoms").ModifierContribution.cannotAttack}.
   */
  cannotAttack: string | undefined;
  /**
   * `coverToAllies` (the Goliath's Guardian) — the cover degree the carrier grants its allies, or
   * `undefined`. Carrier-self and side-agnostic, like `cannotAttack`. See
   * {@link import("./atoms").ModifierContribution.coverToAllies}.
   */
  coverToAllies: "hard" | undefined;
  /** `deniesSoftCover` (Markerlight's mark) — the entity id of the first modifier carrying it. Carrier-self. */
  deniesSoftCover: string | undefined;
  /** `softCover` (Skirmisher 1, Combined Arms 1) — the entity id of the first modifier granting it. Carrier-self. */
  softCover: string | undefined;
  /**
   * `chooseInvoluntaryHeading` (Sea Legs) — the entity id of the first modifier this fold applied
   * that lets the carrier pick where someone else's push takes it, or `undefined`. Carrier-self and
   * side-agnostic, like `cannotAttack`. See
   * {@link import("./atoms").ModifierContribution.chooseInvoluntaryHeading}.
   */
  chooseInvoluntaryHeading: string | undefined;
  /** `rangeBonus` (Siege Stabilizers), summed. Carrier-self. */
  rangeBonus: number;
  /** `minimumRange` (Siege Stabilizers), the largest. Carrier-self. */
  minimumRange: number | undefined;
  /** `cannotMelee` (Siege Stabilizers) — the entity id of the first modifier carrying it. Carrier-self. */
  cannotMelee: string | undefined;
  /** `ramsLarger` (Synthetic Muscle Netting) — the entity id of the first modifier carrying it. Carrier-self. */
  ramsLarger: string | undefined;
  /** `actionCost` (Black Thumb 1) — each re-priced core action, first held override wins, with the entity id that grants it. Carrier-self. */
  actionCosts: { action: string; activation: "quick" | "free"; source: string }[];
}

/** A `modifier` effect bound to the status/entity name it belongs to (for consume-on-hit). */
export type BoundModifier = {
  /** The entity name (status name) this modifier rides — what `consumeOnHit` removes. */
  name: string;
  /** The combatant the modifier is *on* (its carrier). */
  carrier: string;
  effect: ModifierEffect;
  /**
   * The bound partner, when this modifier was
   * gathered off a status INSTANCE carrying `StatusInstance.partner` (Type-3 Projected Shield's
   * planted status) — the `attacker_is`/`target_is` `"partner"` keyword resolves against this.
   * Populated by `gatherModifiers` (`loader.ts`) only; every other modifier source leaves it
   * `undefined`.
   */
  partner?: string;
}

/**
 * Fold a list of {@link BoundModifier}s into a {@link ModifierContributionSum},
 * evaluating each modifier's `condition` against `ctx`. A modifier whose condition
 * fails contributes nothing. `attackerSide` selects whether to read the modifiers as
 * the *attacker's own* (their `attackerAccuracy` applies) or the *target's* (their
 * `targetAccuracy` + `consumeOnHit` apply) — the resolver folds the attacker's
 * modifiers and the target's modifiers separately.
 */
export function foldModifiers(
  mods: readonly BoundModifier[],
  side: "attacker" | "target",
  ctx: PredicateContext,
): ModifierContributionSum {
  const out: ModifierContributionSum = {
    attackerAccuracy: 0,
    targetAccuracy: 0,
    accuracyContributions: [],
    consumeOnHit: [],
    consumedResourceCaps: [],
    ignoreTargetArmor: false,
    doubleDamage: false,
    saveCheckBonus: 0,
    saveCheckContributions: [],
    consumeOnSaveCheck: [],
    advisories: [],
    spatialRidersDeferred: [],
    immunity: [],
    resistance: [],
    autoFailChecks: [],
    armorPiercing: false,
    bonusDamageType: undefined,
    ignoreCover: false,
    substituteStructureDamage: false,
    cannotAttack: undefined,
    coverToAllies: undefined,
    deniesSoftCover: undefined,
    softCover: undefined,
    chooseInvoluntaryHeading: undefined,
    rangeBonus: 0,
    minimumRange: undefined,
    cannotMelee: undefined,
    ramsLarger: undefined,
    actionCosts: [],
  };
  for (const m of mods) {
    // A modifier gathered off a partner-carrying status instance (Type-3 Projected Shield)
    // evaluates its OWN `partner` — merged in per-modifier since a carrier can hold several
    // relationship statuses with different partners at once.
    const mctx: PredicateContext = m.partner !== undefined ? { ...ctx, actors: { ...ctx.actors, partner: m.partner } } : ctx;
    // A modifier's event-fact gate (`match`, always attack-family) evaluates BEFORE
    // `condition` — mirrors `fireTrigger`'s own family→match→condition order (`triggerBus.ts`).
    if (m.effect.match && !evaluateMatch(m.effect.match, mctx.event)) continue;
    // `modifier_consume_resource_on_apply`: a capped modifier gates independently of its own
    // `condition` — at (or past) the cadence cap, the WHOLE modifier contributes nothing this
    // fold, never a partial fold. Availability is checked BEFORE `condition`: a spent cap refuses
    // the modifier whatever the condition says, so evaluating it would only raise a pointless
    // prompt (an unanswered `gm_ruling` halts an interactive resolution — Tactician 2's elevation
    // question on every ranged attack after the 1/round bonus landed). The consume is recorded
    // only AFTER `condition` passes, so an unmet condition still spends nothing.
    const cap = m.effect.mod.consumeResourceOnApply;
    if (cap && !affordable({ kind: "resource_cap_use", carrier: m.carrier, key: resourceCapRawKey(cap), max: cap.max }, ctx.state, ctx.costs)) continue;
    if (m.effect.condition && !evaluatePredicate(m.effect.condition, mctx)) continue;
    const c = m.effect.mod;
    if (cap) out.consumedResourceCaps.push({ carrier: m.carrier, key: resourceCapRawKey(cap) });
    // Deferred spatial riders are informational on EITHER side (the resolver surfaces both the
    // attacker's and the target's, prefixed by side).
    if (c.spatialRiderDeferred) out.spatialRidersDeferred.push(c.spatialRiderDeferred);
    // The save/check boon (Bolster) is a CARRIER-SELF contribution — a save is rolled by
    // one combatant, so it has no attacker/target side. Accumulate it side-agnostically
    // (like the spatial rider above); the save resolver folds the carrier's own modifiers
    // and reads these regardless of which side label it passed.
    if (c.saveCheckBonus) {
      out.saveCheckBonus += c.saveCheckBonus;
      out.saveCheckContributions.push({ name: m.name, carrier: m.carrier, value: c.saveCheckBonus });
    }
    if (c.consumeOnSaveCheck) out.consumeOnSaveCheck.push(m.name);
    // Immunity is likewise a CARRIER-SELF trait — accumulate side-agnostically, exactly like
    // the save/check boon above.
    if (c.immunity) out.immunity.push(...c.immunity);
    // Resistance is likewise a CARRIER-SELF trait — accumulate side-agnostically. `"all"`
    // pushes the sentinel; a `DamageType[]` pushes each named type.
    if (c.resistance) {
      if (c.resistance === "all") out.resistance.push("all");
      else out.resistance.push(...c.resistance);
    }
    // Stunned's auto-fail HULL/AGI is likewise a CARRIER-SELF trait — accumulate
    // side-agnostically, exactly like the save/check boon / immunity / resistance above.
    if (c.autoFailChecks) out.autoFailChecks.push(...c.autoFailChecks);
    // The rig's Unarmed — likewise a CARRIER-SELF trait.
    if (c.cannotAttack && out.cannotAttack === undefined) out.cannotAttack = m.name;
    // The Goliath's Guardian — likewise a CARRIER-SELF trait.
    if (c.coverToAllies) out.coverToAllies = c.coverToAllies;
    // Markerlight's mark — likewise a CARRIER-SELF trait.
    if (c.deniesSoftCover && out.deniesSoftCover === undefined) out.deniesSoftCover = m.name;
    // Skirmisher 1 / Combined Arms 1 — likewise a CARRIER-SELF trait.
    if (c.softCover && out.softCover === undefined) out.softCover = m.name;
    // Sea Legs — likewise a CARRIER-SELF trait.
    if (c.chooseInvoluntaryHeading && out.chooseInvoluntaryHeading === undefined) out.chooseInvoluntaryHeading = m.name;
    // Siege Stabilizers — likewise CARRIER-SELF traits.
    if (c.rangeBonus) out.rangeBonus += c.rangeBonus;
    if (c.minimumRange) out.minimumRange = Math.max(out.minimumRange ?? 0, c.minimumRange);
    if (c.cannotMelee && out.cannotMelee === undefined) out.cannotMelee = m.name;
    if (c.ramsLarger && out.ramsLarger === undefined) out.ramsLarger = m.name;
    if (c.actionCost && !out.actionCosts.some((a) => a.action === c.actionCost!.action)) out.actionCosts.push({ ...c.actionCost, source: m.name });
    if (side === "attacker") {
      if (c.attackerAccuracy) {
        out.attackerAccuracy += c.attackerAccuracy;
        out.accuracyContributions.push({ name: m.name, carrier: m.carrier, side: "attacker", value: c.attackerAccuracy });
      }
      // Advisories surface only when the carrier is the ATTACKER — the action/movement gates
      // warn the acting combatant.
      if (c.advisory) out.advisories.push(c.advisory);
      // ATTACKER-side: the acting combatant's own talent/ammo choice, not something a target inflicts.
      if (c.armorPiercing) out.armorPiercing = true;
      if (c.bonusDamageType !== undefined) out.bonusDamageType = c.bonusDamageType;
      if (c.overrideDamageType !== undefined) out.overrideDamageType = c.overrideDamageType;
      if (c.ignoreCover) out.ignoreCover = true;
    } else {
      if (c.targetAccuracy) {
        out.targetAccuracy += c.targetAccuracy;
        out.accuracyContributions.push({ name: m.name, carrier: m.carrier, side: "target", value: c.targetAccuracy });
      }
      if (c.consumeOnHit) out.consumeOnHit.push(m.name);
      if (c.ignoreTargetArmor) out.ignoreTargetArmor = true;
      if (c.doubleDamage) out.doubleDamage = true;
      // Stunned's Evasion cap — a TARGET-side trait (an attack against the carrier reads the
      // capped value). Multiple sources combine as the most restrictive (lowest) cap.
      if (c.evasionCap !== undefined) {
        out.evasionCap = out.evasionCap === undefined ? c.evasionCap : Math.min(out.evasionCap, c.evasionCap);
      }
      // Sniper's Mark's structure substitution — a TARGET-side trait (the carrier is the one being
      // shot, like Shredded/Exposed above). Boolean OR: two marks on one target still substitute
      // exactly one structure point.
      if (c.substituteStructureDamage) out.substituteStructureDamage = true;
    }
  }
  return out;
}

/** The in-flight save/check's own cause/stat, matched against a modifier's `mod.saveScope`. */
export type SaveScopeContext = {
  cause?: string;
  stat?: string;
  roll?: SaveCheckRoll;
};

/**
 * Strip a scoped modifier's save/check facets when the modifier's own `mod.saveScope` doesn't
 * match `context`. A `saveScope` field only "counts" a dimension it actually authors:
 *
 * - `saveScope` absent entirely → unscoped, untouched regardless of `context` (Bolster's `+2`).
 * - `saveScope.cause` set, `.stat` absent → matches iff `context.cause === saveScope.cause`
 *   (ms_stable_structure's Prone/Knockback scope).
 * - `saveScope.stat` set, `.cause` absent → matches iff `context.stat === saveScope.stat`
 *   (t_ace:1's stat-scoped save bonus).
 * - `saveScope.roll` set → matches iff the site's roll is that kind (Disable Life Support's
 *   saves-only `+1 Difficulty`). It ANDs with the other two like they AND with each other.
 * - **Both set → AND** — matches only when BOTH dimensions match (no shipped carrier authors
 *   both today; see `interpret.test.ts`'s `saveScope — both cause AND stat` describe block
 *   for the direct unit proof).
 *
 * A caller that doesn't know one dimension (e.g. `saves.ts`'s `save_ends` re-save has no
 * per-instance record of which HASE stat originally forced it, and never threads a `cause` at
 * all) simply omits that key from `context` — `undefined` never matches a DEFINED
 * `saveScope` field, so an unproven dimension always strips rather than over-granting.
 *
 * Exported so `saves.ts`'s `resolveSaveEnds` and `contest.ts`'s `rollSide` can apply the SAME
 * strip `interpretForceSaveRoll` does — all three sites gather the carrier's save/check
 * modifiers and must strip a scoped one identically, or a talent/system-borne scoped boon
 * applies unscoped at whichever site skipped the strip. Only the
 * `saveCheckBonus`/`consumeOnSaveCheck` facets are stripped on a
 * mismatch — every OTHER facet the same modifier might carry (immunity, autoFailChecks, an
 * advisory, …) folds normally, so this never silently blanks an unrelated contribution.
 */
export function scopeSaveModifiers(mods: readonly BoundModifier[], context: SaveScopeContext): BoundModifier[] {
  return mods.map((m) => {
    const scope = m.effect.mod.saveScope;
    if (scope === undefined) return m;
    const causeMatches = scope.cause === undefined || scope.cause === context.cause;
    const statMatches = scope.stat === undefined || scope.stat === context.stat;
    const rollMatches = scope.roll === undefined || scope.roll === context.roll;
    if (causeMatches && statMatches && rollMatches) return m;
    const { saveCheckBonus: _saveCheckBonus, consumeOnSaveCheck: _consumeOnSaveCheck, ...rest } = m.effect.mod;
    return { ...m, effect: { ...m.effect, mod: rest } };
  });
}

/** The in-flight attack's own attack verb, matched against a modifier's `mod.attackScope`. */
export type AttackScopeContext = {
  verb?: AttackVerb;
};

/**
 * The **attack-resolution facet domain** — every {@link ModifierContribution} facet
 * {@link scopeAttackModifiers} strips on a verb mismatch.
 *
 * **The membership rule, stated once so a new facet has an answer rather than a default:** a
 * facet is in this domain iff *attack resolution is what consumes it* — i.e. the `resolve/` attack spine
 * reads it back off one of the two attack folds (`atkMods`/`tgtMods`, pre-roll) and turns it
 * into an observable of the resolution. This is the exact analogue of
 * {@link scopeSaveModifiers} stripping its WHOLE domain (both `saveCheckBonus` AND
 * `consumeOnSaveCheck`, not just the number): a modifier the author scoped to a verb must
 * contribute **nothing attack-related** when the verb doesn't match, because a partial strip
 * is a silent over-grant — the clause reads as correctly scoped and isn't.
 *
 * Each member, and why it qualifies:
 *
 * - `attackerAccuracy` — the to-hit boon verb-scoping exists for; read off `atkMods`. **Live.**
 * - `overrideDamageType`, `ignoreCover` — the other attacker-side facets the `resolve/` spine reads
 *   off `atkMods` (`t_walking_armory:1`'s ammo, `t_vanguard:2`'s cover). **Live.**
 * - `advisory` — read off `atkMods.advisories` into the resolution's `warnings`, and that is
 *   its ONE read site anywhere in the engine, so it is attack-resolution output like any
 *   other. Stripping it is not "swallowing a warning": a verb-scoped clause whose verb didn't
 *   match contributed nothing, so an advisory narrating that contribution on a DIFFERENT
 *   attack is actively misleading, not merely noisy. **Live.**
 * - `spatialRiderDeferred` — same argument as `advisory` (read off `atkMods`/`tgtMods` into
 *   `warnings`, no other read site). Its job is to flag that a modifier's math here is
 *   partial; a stripped modifier has no math here to be partial about. **Live.**
 * - `armorPiercing`, `bonusDamageType` — Walking Armory's SABOT and HELLFIRE, read off `atkMods`. **Live.**
 * - `consumeResourceOnApply` — not a grant but the fold's own per-cadence charge gate: an
 *   unstripped one **burns a charge** for a modifier that then contributes nothing (the
 *   mirror-image defect of an over-grant, and just as silent). Safe to strip here because the
 *   only facets left behind are the non-attack ones below, and the `resolve/` spine never reads any of
 *   those back off an attack fold — so dropping the gate can't hand out a free grant.
 * - `targetAccuracy`, `consumeOnHit`, `ignoreTargetArmor`, `doubleDamage`, `evasionCap`,
 *   `substituteStructureDamage` — the six TARGET-side attack facets
 *   (`substituteStructureDamage` joined them in `npc-features-fielded-roster` Phase 8.1: attack
 *   resolution is what consumes it — `applyHitDamage` reads it off the S3 target fold and turns it
 *   into the hit's structure point — so the membership rule above puts it here, and it follows the
 *   others' precedent rather than inventing a new one). **Inert on this array today**, and said
 *   plainly rather than implied: `scopeAttackModifiers` is currently applied only to the
 *   attacker-side list (`atkMods`), and `foldModifiers(…, "attacker")` never reads
 *   any of them, so stripping them is a no-op right now. They are listed anyway because
 *   they are unambiguously attack-resolution facets by the rule above, and the one plausible
 *   next step — applying this strip to `tgtMods` so a defensive clause can be verb-scoped too
 *   — must not have to remember to widen a hand-listed destructure to stay correct. Including
 *   an inert facet costs nothing; excluding a live one is a silent over-grant.
 *
 * Deliberately **NOT** in the domain (the complement is listed in full, and pinned exhaustive
 * against `keyof ModifierContribution` just below, so a newly-added facet is a compile error
 * rather than a default):
 *
 * - `saveCheckBonus`, `consumeOnSaveCheck`, `saveScope` — the save/check domain, owned by
 *   {@link scopeSaveModifiers}. An attack verb has nothing to say about a save.
 * - `immunity`, `resistance`, `autoFailChecks` — carrier-self defensive traits whose real read
 *   sites are `seed.ts`'s `foldWide` (the composed `gatherModifiersAt` walk behind all five
 *   Immunity/Resistance predicates) / `interpretForceSave` / `contest.ts`, none of which
 *   passes through this strip. `foldModifiers` accumulates them on an attack fold but
 *   the `resolve/` spine never reads them back off `atkMods`/`tgtMods`, so they fail the membership
 *   rule twice over. Blanking a mech's Immunity because its Ram-scoped accuracy buff didn't
 *   apply to this Skirmish would be a category error.
 * - `cannotAttack` — a carrier-self legality trait read by `seed.ts`'s `cannotMakeAttacks` (the
 *   same `foldWide`), never off an attack fold. A Ram-scoped buff failing to apply must not arm
 *   or disarm a combatant.
 * - `deniesSoftCover` — a carrier-self trait read by `seed.ts`'s `deniesSoftCover` for the cover query.
 * - `softCover` — a carrier-self trait read by `seed.ts`'s `grantsSoftCover` for the cover query.
 * - `coverToAllies` — a carrier-self trait read by `seed.ts`'s `alliedCoverCells` (the same
 *   `foldWide`) for the cover query, never off an attack fold.
 * - `chooseInvoluntaryHeading` — a carrier-self trait read by `seed.ts`'s
 *   `choosesInvoluntaryHeading` (the same `foldWide`) at a push, never off an attack fold.
 * - `ignoreDifficulty` — read off the firing weapon's own entry by the to-hit, never off a fold.
 * - `damageEqualsDistance` / `reliableUnlessStructure` / `attacksTargets` / `lineToTarget` — read
 *   off the firing weapon's own entry by the attack spine (`resolve/weaponOwnDamage.ts`), never off
 *   a fold. `usableWhileJammed` likewise, by the status-legality gate's caller.
 * - `sparesAllies` / `areaOnHit` — read off the firing weapon's own entry by the area fan-out, never off a fold.
 * - `rangeBonus` / `minimumRange` / `cannotMelee` / `ramsLarger` — carrier-self legality traits read by `seed.ts`'s
 *   `attackStance` (the same `foldWide`) at the attack gates, never off an attack fold.
 * - `actionCost` — a carrier-self re-price read by `seed.ts`'s `actionCostOverride` (the same
 *   `foldWide`) when an intent is priced, never off an attack fold.
 * - `tag` — a `{VAL}` rider read ONLY by `coverage.ts`'s static clause classifier, never by
 *   any runtime fold. Not a contribution at all.
 * - `attackScope` itself — the scope marker being matched on, not something it grants.
 */
export const ATTACK_SCOPED_FACETS = [
  "attackerAccuracy",
  "overrideDamageType",
  "ignoreCover",
  "advisory",
  "spatialRiderDeferred",
  "armorPiercing",
  "bonusDamageType",
  "consumeResourceOnApply",
  "targetAccuracy",
  "consumeOnHit",
  "ignoreTargetArmor",
  "doubleDamage",
  "evasionCap",
  "substituteStructureDamage",
] as const satisfies readonly (keyof ModifierContribution)[];

/**
 * The explicit complement of {@link ATTACK_SCOPED_FACETS} — the facets a verb mismatch leaves
 * ALONE. Its only job is to make {@link ATTACK_SCOPED_FACETS}'s exhaustiveness pin below
 * meaningful: without a declared complement, "every facet is classified" would be vacuous.
 */
const NON_ATTACK_SCOPED_FACETS = [
  "attackScope",
  "saveScope",
  "saveCheckBonus",
  "consumeOnSaveCheck",
  "immunity",
  "resistance",
  "autoFailChecks",
  "cannotAttack",
  "coverToAllies",
  "deniesSoftCover",
  "softCover",
  "chooseInvoluntaryHeading",
  "rangeBonus",
  "minimumRange",
  "cannotMelee",
  "ramsLarger",
  "actionCost",
  "ignoreDifficulty",
  "damageEqualsDistance",
  "reliableUnlessStructure",
  "attacksTargets",
  "lineToTarget",
  "usableWhileJammed",
  "sparesAllies",
  "areaOnHit",
  "tag",
] as const satisfies readonly (keyof ModifierContribution)[];

/** Errors unless `T` is `never` — the carrier for the exhaustiveness pin just below. */
type AssertNever<T extends never> = T;

/**
 * **Compile-time exhaustiveness pin, on the INPUT side.** Every key of
 * {@link ModifierContribution} must be classified as either attack-scoped or explicitly not —
 * so adding a facet to `ModifierContribution` fails `bun run typecheck` with an unclassified
 * key rather than silently defaulting to "not stripped," which is the over-grant direction.
 *
 * Pinning the SOURCE type (`keyof ModifierContribution`) rather than the strip's output is
 * deliberate: an output-shape pin only ever sees the keys the list already names, so a NEW
 * source field slips past it untouched. The two `satisfies` clauses above cover the other
 * direction (no listed name can drift off the type), so the pair is closed both ways.
 */
type _EveryModifierFacetClassified = AssertNever<
  Exclude<keyof ModifierContribution, (typeof ATTACK_SCOPED_FACETS)[number] | (typeof NON_ATTACK_SCOPED_FACETS)[number]>
>;

/**
 * Strip a scoped modifier's **whole attack-resolution facet domain**
 * ({@link ATTACK_SCOPED_FACETS}) when the modifier's own `mod.attackScope` doesn't match the
 * in-flight attack's own attack verb (t_juggernaut:1's "after you Boost, your next Ram gets +1
 * Accuracy," an action-scoped one-shot buff).
 *
 * - `attackScope` absent entirely → unscoped, untouched regardless of `context` (every OTHER
 *   `attackerAccuracy` carrier's pre-existing, unqualified behavior).
 * - `attackScope.verb` set → matches iff `context.verb === attackScope.verb`. `undefined`
 *   never matches a DEFINED `verb` — a caller that can't derive the in-flight verb (or hasn't
 *   threaded one through) always strips rather than risk an over-grant.
 *
 * **This mirrors {@link scopeSaveModifiers}'s discipline at the level that actually matters:
 * each strips its ENTIRE domain, not one facet of it.** `scopeSaveModifiers` drops both
 * save/check facets and leaves every non-save facet alone; this drops every attack-resolution
 * facet and leaves every non-attack facet alone. The two domains differ in size (two facets
 * vs. fourteen) because attack resolution simply has more surface — not because this scope is
 * narrower in kind. See {@link ATTACK_SCOPED_FACETS} for the per-facet membership argument,
 * including which members are live today and which are deliberately inert.
 *
 * Applied to the FULL attacker-side modifier list `resolve/attackModifiers.ts` folds (status + weapon/tag +
 * pilot-effect + system + conferred — every vector `attackerAccuracy` itself already reaches),
 * so every {@link import("./coverage").ReachabilityKind} that reaches `attackerAccuracy`
 * through that fold reaches `attackScope` identically — see `coverage.ts`'s
 * `REACHABLE_MODIFIER_FACETS` for the traced per-kind claim. A roll replacement (advantage,
 * maximize) scopes itself to a verb with `match.verb` instead.
 */
export function scopeAttackModifiers(mods: readonly BoundModifier[], context: AttackScopeContext): BoundModifier[] {
  return mods.map((m) => {
    const scope = m.effect.mod.attackScope;
    if (scope === undefined) return m;
    if (scope.verb === context.verb) return m;
    const mod: ModifierContribution = { ...m.effect.mod };
    for (const facet of ATTACK_SCOPED_FACETS) delete mod[facet];
    return { ...m, effect: { ...m.effect, mod } };
  });
}

/** Build a {@link PredicateContext} for a one-attacker-vs-one-target attack resolution. */
export type AttackPredicateContextArgs = {
  state: EncounterState;
  attacker: string;
  target: string;
  /** Where a `gm_ruling` in a modifier's condition reads its answer: the attack's own `choices`. */
  rulings?: ChoiceSource;
  /**
   * Whether this resolution is a ranged attack (Engaged's ranged-only gate, the
   * `ranged_attack` predicate's one source). The
   * attack resolver computes this the SAME way the cover fold does
   * (`isRangedAttack(weapon.range) && !intent.tech`) and passes it through; every
   * other caller (saves, contests, activate actions — no weapon in play) omits it,
   * which reads as `false` (the predicate stays dark, exactly as intended).
   */
  rangedAttack?: boolean;
  /**
   * True iff this resolution is a tech attack (the `tech_attack` predicate's one source, the
   * hacker talent's "when you hit with a tech attack" gate). The attack resolver reads it off
   * `intent.tech`, the SAME field
   * `rangedAttack`'s own `!intent.tech` posture reads, and passes it through the SAME
   * way it threads `rangedAttack`; every other caller (saves, contests — no attack in
   * progress) omits it, which reads as `false` (the predicate stays dark, exactly as
   * intended).
   */
  techAttack?: boolean;
  /**
   * The firing weapon's class (the `weapon_type` predicate's one source,
   * crack_shot/stormbringer's "ranged attack with a Rifle"
   * shape). The attack resolver reads it off the resolved weapon's frozen
   * `WeaponProfile.type` and passes it through the SAME way it threads `rangedAttack`;
   * every other caller (saves, contests — no mech weapon in play) omits it.
   */
  weaponType?: string;
  /**
   * The firing weapon's mount size (the `weapon_mount_class` predicate's one source,
   * duelist/gunslinger's "Main Melee weapon"/"Auxiliary ranged weapon" shape). The attack
   * resolver reads it off the
   * resolved weapon's ALREADY-frozen `WeaponProfile.mount` (the same field the
   * Barrage/Superheavy gates read) and passes it through the SAME way it threads
   * `weaponType`; every other caller (saves, contests — no mech weapon in play) omits
   * it.
   */
  mount?: string;
  /**
   * Whether this is the first attack the attacker has declared THIS TURN (the
   * `first_attack_this_turn` predicate's one source, duelist/gunslinger's "the first … attack
   * you make … on your turn" shape). The attack
   * resolver reads it off the attacker's per-turn attack-ordinal counter (`ATTACK_ORDINAL_RESOURCE`,
   * read BEFORE this attack's own increment) and passes it through the SAME way it threads
   * `weaponType`/`mount`; every other caller (saves, contests — no attacker-declared attack in
   * play) omits it.
   */
  firstAttackThisTurn?: boolean;
  /**
   * True iff the resolution's to-hit roll drew a natural 20 (the `natural_20` predicate's one
   * source). Unlike the four facts above (all knowable
   * BEFORE the roll), this one is only knowable AFTER — so the ONE caller that supplies it
   * is the damage roll's replacement gather (`damageRollFacts`, `resolve/attackModifiers.ts`),
   * never the pre-roll `atkMods`/`tgtMods` fold (which happens before the d20 is drawn). Every other caller omits it,
   * which reads as `false` (the predicate stays dark pre-roll, exactly as intended).
   */
  natural20?: boolean;
  /**
   * The **cover degree the TARGET stands in** for this attack (`npc-features-fielded-roster`
   * Phase 8.1) — the `AttackMatch.cover` fact's one source for both attacker- and target-side
   * modifier folds. The attack resolver reads it straight off the S2.5 cover fold ({@link
   * import("../resolve/attackModifiers").foldAttackCover}'s `degree`), which is why that fold
   * now runs BEFORE the modifier fold rather than after it. Every other caller (saves,
   * contests, the EV toolkit's probe — no cover fold in play) omits it, which reads as absent
   * on the event and so never matches an authored `cover` value, including `"none"`: an omitted
   * fact is unknown, not "no cover".
   */
  cover?: CoverDegree;
  /**
   * The **composite granted-action id** the in-flight attack fires as (`"ms_markerlight:0"`, or
   * an ordinary attack's own weapon id) — `attackHandlerId`'s answer, threaded so a `modifier`
   * clause can gate on `match.actionId` the way an on-hit `triggered` clause already can
   * (`npc-features-fielded-roster` Phase 8.1). Without it a modifier folded during an attack saw
   * no weapon identity at all, so "this modifier applies only when firing THIS weapon" was
   * inexpressible and a carrier-scoped clause silently applied to every attack the carrier made.
   * The attack resolver reads it off {@link import("../resolve/attackFacts").AttackFacts.actionId}
   * — the SAME single derivation the declared/resolved trigger fires use. Every other caller
   * (saves, contests, the EV toolkit's probe) omits it.
   */
  actionId?: string;
  /**
   * **How many characters the in-flight attack targets** — the `AttackMatch.targetCount` fact's
   * source for the attacker/target modifier folds, so a `modifier` clause can gate on
   * `{ ">": 1 }` ("an attack that targets more than one character") against the SAME number a
   * `{TARGET_COUNT}` magnitude scales by. The attack resolver reads it off
   * `resolveSingleTarget`'s own count — the area fan-out's settled `targetIds.length` threaded
   * down per sub-resolution, or `1` on the single-target spine — which is why every fold this
   * caller runs sees a real count rather than an absence. Every other caller (saves, contests,
   * the EV toolkit's probe — no attack fan-out in play) omits it, which reads as absent on the
   * event and so never matches an authored `targetCount` value, including `1`.
   */
  targetCount?: number;
  /**
   * The statuses this attack consumes off its target (`settleConsumeChoice`, pre-roll) — the
   * `AttackMatch.consumes` fact. Every non-attack caller omits it.
   */
  consumes?: string[];
  /**
   * The resolution-scoped **fact ledger's** `choose` record —
   * the `chosen` {@link PredicateContext} field's one source for the pre-roll attacker/
   * target modifier fold (`resolve/attackModifiers.ts`'s `atkMods`/`tgtMods`, the SAME ledger
   * `fireAttackDeclared` threads onto its own `FireContext` so an `on_attack_declared`
   * `choose` atom's decision is already recorded by the time this fold runs). Every other
   * caller (saves, contests — no attack in progress) omits it, which reads as no match for
   * every `chose` value.
   */
  chosen?: Record<string, string>;
  /**
   * The attack's verb, and the outcome's `hit`/`crit`: facts only a roll replacement's `match`
   * reads (`events/rollReplacements.ts`). The attack roll's facts carry the verb; a damage roll's
   * also carry the outcome that caused it. The modifier fold omits all three.
   */
  verb?: AttackVerb;
  hit?: boolean;
  crit?: boolean;
  /** The action's pending costs ({@link PredicateContext.costs}). */
  costs?: PredicateContext["costs"];
};

export function attackPredicateContext(args: AttackPredicateContextArgs): PredicateContext {
  const { state, attacker, target, rulings, rangedAttack, techAttack, weaponType, mount, firstAttackThisTurn, natural20, chosen } = args;
  const { cover, actionId, targetCount, consumes, costs, verb, hit, crit } = args;
  // The caller-facing kwargs stay flat; the returned `PredicateContext` collapses them into
  // one typed `AttackEvent`, same as `predicateContextFrom`'s own collapse. `phase` is inferred
  // from `natural20`'s presence (see its doc: only the damage roll's facts supply it) — `undefined` ⇒ pre-roll ⇒ `"declared"`; present ⇒ `"resolved"`.
  const event: AttackEvent = {
    family: "attack",
    phase: natural20 !== undefined ? "resolved" : "declared",
    ranged: rangedAttack,
    tech: techAttack,
    weaponType,
    mount,
    firstAttackThisTurn,
    natural20,
    cover,
    actionId,
    targetCount,
    ...(consumes !== undefined ? { consumes } : {}),
    ...(verb !== undefined ? { verb } : {}),
    ...(hit !== undefined ? { hit } : {}),
    ...(crit !== undefined ? { crit } : {}),
  };
  return { state, actors: { self: attacker, target, attacker }, ...(rulings !== undefined ? { rulings } : {}), event, chosen, ...(costs !== undefined ? { costs } : {}) };
}
